"use client";
import { BlockId } from "block-system/types";
import { ChatbotEmbedIcon } from "./ChatbotEmbedIcon";
import { useState, ReactNode, useEffect } from "react";
import { useChatbot } from "./ChatbotContext";
import { ProjectAppearance } from "server/schemas/projects";
import { ChatbotBlock as ChatbotBlockType } from "../types";
import { cn } from "utils/cn";
import { Suspense } from "react";
import dynamic from "next/dynamic";
import { ChatbotDisabledEmbeddedPopup } from "./ChatbotDisabled";
import { useInterfacesTheme } from "lib/theme/ThemeProvider";
import styled from "styled-components";
import { Icon } from "@zapier/zinnia-icons";
import {
  getEmbeddedChatbotState,
  setEmbeddedChatbotState,
} from "@zapier/chatbots-tailwind";
import { usePrevious } from "lib/hooks";

const ChatbotTailwind = dynamic(() => import("./ChatbotTailwind"), {
  ssr: false,
});

// Declare the exact target width where we'll flip between a full-screen
// view and a popup bubble view. The current value was chosen for two reasons:
// 1. It's unlikely to be the native width of any device, so it shouldn't
//    trigger a false positive view.
// 2. It allowed for a popup bubble width that looks good 😄
// Note: Keep this in sync with the media query in
// zapier-interfaces-chatbot-embed.css, with the hard-coded values in
// StyledPopup below, and with styles in @zapier/chatbots-tailwind.
const targetWidth = 409;

type Props = {
  fallback: ReactNode;
  projectId: string;
  pageId: string;
  block: ChatbotBlockType;
  blockId: BlockId;
  projectAppearance?: ProjectAppearance;
};

const StyledChatbotPopupBlock = styled.div<{
  $embedIconHeight: number;
}>`
  position: fixed;
  bottom: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  height: 100%;
  width: 100%;

  @media (min-width: ${targetWidth}px) and (max-width: ${targetWidth}px) {
    right: 1rem;
    bottom: ${({ $embedIconHeight }) => $embedIconHeight + 30}px;
    top: 0;
    left: 1rem;
    width: auto;
    height: auto;
  }
`;

const StyledPopup: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div"> & {
    $isHidden: boolean;
  }
> = ({ $isHidden, ...props }) => {
  const interfacesTheme = useInterfacesTheme();
  return (
    <div
      {...props}
      className={cn(
        "relative flex w-full grow origin-[90%_120%] overflow-hidden shadow-lg transition duration-700 ease-out min-[409px]:max-[409px]:border",
        {
          "scale-50 opacity-0": $isHidden,
          "scale-100 opacity-100": !$isHidden,
          "min-[409px]:max-[409px]:rounded-xl": !interfacesTheme,
          "min-[409px]:max-[409px]:rounded-large": interfacesTheme,
        },
        props.className
      )}
    />
  );
};

const StyledButtonContainer = styled.div<{ $isHidden: boolean }>`
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.625rem;
  display: ${({ $isHidden }) => ($isHidden ? "block" : "none")};
  transition: transform 0.2s ease-in-out;

  @media (min-width: ${targetWidth}px) and (max-width: ${targetWidth}px) {
    display: block;
  }

  &:hover {
    transform: scale(1.1);
  }
`;

const GreetingBlockContainer = styled.div<{ $embedIconHeight: number }>`
  position: absolute;
  bottom: ${({ $embedIconHeight }) => $embedIconHeight + 25}px;
  right: 1rem;
  max-width: 300px;
`;

const GreetingBlockStyle = (
  props: React.ComponentPropsWithoutRef<"button">
) => {
  const interfacesTheme = useInterfacesTheme();

  return (
    <button
      {...props}
      className={cn(
        "p-5",
        "cursor-pointer",
        "w-full",
        "animate-fadeIn",
        {
          "rounded-large rounded-br-none bg-card text-card-foreground [&_pre]:rounded-large":
            interfacesTheme,
          "rounded-lg rounded-br-none bg-zi-primary-100 [&_pre]:rounded-lg":
            !interfacesTheme,
        },
        props.className
      )}
    />
  );
};

const GreetingCloseStyle = (
  props: React.ComponentPropsWithoutRef<"button">
) => {
  const interfacesTheme = useInterfacesTheme();

  return (
    <button
      {...props}
      className={cn(
        "flex",
        "justify-start",
        "absolute",
        "right-1",
        "top-1",
        "cursor-pointer",
        "pb-1",
        {
          "text-card-foreground": interfacesTheme,
        },
        props.className
      )}
    />
  );
};

const GreetingTextStyle = (props: React.ComponentPropsWithoutRef<"span">) => {
  const interfacesTheme = useInterfacesTheme();

  return (
    <span
      {...props}
      className={cn(
        "overflow-ellipsis",
        "break-words",
        "overflow-hidden",
        "box-border",
        "line-clamp-2",
        "self-end text-base leading-[1.5] drop-shadow-[0_15px_25px_rgba(0,0,0,0.02)] min-[326px]:max-[326px]:text-[14.5px] md:p-5 md:text-[14.5px]",
        {
          "rounded-large text-left": interfacesTheme,
          "rounded-lg rounded-br-none": !interfacesTheme,
        },
        props.className
      )}
    />
  );
};

export function EmbeddedChatbotBlockPopup(props: Props) {
  const [isHidden, setIsHidden] = useState(true);
  const [deviceWidth, setDeviceWidth] = useState(0);
  const [showNudge, setShowNudge] = useState(false);
  const [hasBeenNudgedBefore, setHasBeenNudgedBefore] = useState(false);
  const wasHidden = usePrevious(isHidden);

  const { status, initChatbotResponse } = useChatbot();
  const embedIconHeight = props.block.config.iconSize || 50;

  // update closed dimensions
  useEffect(() => {
    if (embedIconHeight) {
      window.parent.postMessage(
        `updateClosedDimensions::${embedIconHeight}`,
        "*"
      );
    }
  }, [embedIconHeight]);

  const onClickToggleHandler = () => {
    if (props.block.config.nudgeMessage && !props.block.config.openByDefault) {
      setShowNudge(false);
      setHasBeenNudgedBefore(true); // always disregard nudge if icon is clicked
    }
    setIsHidden((prev) => !prev);
  };

  // while the chatbot is loading, the iframe is set to 100% width,
  // so we can get the device width from the window
  useEffect(() => {
    if (deviceWidth === 0 && window.innerWidth !== 0) {
      setDeviceWidth(window.innerWidth);
      window.parent.postMessage("zChatbotClosed", "*");
    }
  }, [deviceWidth]);

  // handle auto-open functionality
  // open if the chatbot was previously open or if the chatbot is
  // set to open by default and the device width is >= 500
  useEffect(() => {
    if (status === "resolved" && !initChatbotResponse?.isLocked) {
      const embeddedChatbotState = getEmbeddedChatbotState();

      // obey the current state of the chatbot, or open by default
      // if there is no current state
      if (
        embeddedChatbotState?.isPopupEmbedOpen ||
        (embeddedChatbotState?.isPopupEmbedOpen !== false &&
          props.block.config.openByDefault &&
          deviceWidth >= 500)
      ) {
        setIsHidden(false);
      }
    }
  }, [
    status,
    initChatbotResponse,
    deviceWidth,
    props.block.config.openByDefault,
  ]);

  // handle open/close toggle
  useEffect(() => {
    if (status !== "resolved") {
      return;
    }

    if (wasHidden !== isHidden) {
      window.parent.postMessage(
        isHidden ? "zChatbotClosed" : "zChatbotOpened",
        "*"
      );
    }

    if (initChatbotResponse?.id) {
      setEmbeddedChatbotState({
        isPopupEmbedOpen: !isHidden,
      });
    }
  }, [status, isHidden, initChatbotResponse?.id, wasHidden]);

  // handle "do not nudge" message
  useEffect(() => {
    if (!showNudge && hasBeenNudgedBefore) {
      window.parent.postMessage("doNotNudge", "*");
    }
  }, [showNudge, hasBeenNudgedBefore]);

  // handle nudge message
  useEffect(() => {
    let timeoutId: any;

    if (
      !initChatbotResponse?.isLocked && // do not show nudge if chatbot is locked
      props.block.config.nudgeMessage &&
      !props.block.config.openByDefault
    ) {
      if (!showNudge && !hasBeenNudgedBefore) {
        timeoutId = setTimeout(() => {
          setShowNudge(true);
          setHasBeenNudgedBefore(true);
          window.parent.postMessage("showNudge", "*");
        }, 5000);
      }
    }

    // clear timeout if icon is clicked to prevent nudge message from showing
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    initChatbotResponse,
    showNudge,
    hasBeenNudgedBefore,
    props.block.config.nudgeMessage,
    props.block.config.openByDefault,
  ]);

  const greetingText = initChatbotResponse?.defaultMessage.includes("\n")
    ? initChatbotResponse?.defaultMessage.split("\n")[0]
    : initChatbotResponse?.defaultMessage;

  return (
    <Suspense fallback={props.fallback}>
      <StyledChatbotPopupBlock $embedIconHeight={embedIconHeight}>
        {!!showNudge && (
          <GreetingBlockContainer $embedIconHeight={embedIconHeight}>
            <GreetingCloseStyle
              onClick={() => {
                setShowNudge(false);
                setHasBeenNudgedBefore(true);
              }}
            >
              <Icon name="formX" size={18} />
            </GreetingCloseStyle>
            <GreetingBlockStyle onClick={onClickToggleHandler}>
              <GreetingTextStyle>{greetingText}</GreetingTextStyle>
            </GreetingBlockStyle>
          </GreetingBlockContainer>
        )}
        <StyledPopup $isHidden={isHidden}>
          <ChatbotTailwind
            {...props}
            handlePopupCloseButtonClick={onClickToggleHandler}
            loadingSlot={() => <>{props.fallback}</>}
            disabledSlot={ChatbotDisabledEmbeddedPopup}
          />
        </StyledPopup>
      </StyledChatbotPopupBlock>
      <StyledButtonContainer $isHidden={isHidden}>
        <ChatbotEmbedIcon
          block={props.block}
          isHidden={isHidden}
          onClickToggleHandler={onClickToggleHandler}
        />
      </StyledButtonContainer>
    </Suspense>
  );
}
